import { useCallback } from "react";

import { SeparableAccumulationItem } from "@/App/api/member";
import { isFamilyCoverage } from "@/App/utils/familyCoverage";

const IN_NETWORK_STRINGS = ["in_network_tier_1"];

// all accumulators in these categories should contain the following strings (theoretically)
const DEDUCTIBLE_STRING = "deductible";
const OUT_OF_POCKET_STRING = "out_of_pocket";
const PLAN_MAXIMUM_STRING = "plan_maximum";
const FERTILITY_STRING = "fertility";

// the big three will have these types exactly
export const DEDUCTIBLE = "deductible";
export const OUT_OF_POCKET = "maximum_out_of_pocket";
export const PLAN_MAXIMUM = "plan_maximum";

const USE_CATEGORY_ACCUMS = false;

export const useAccumulations = () => {
  const inNetworkAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>): Array<SeparableAccumulationItem> => {
    return accumulations?.filter((accum) => IN_NETWORK_STRINGS.includes(accum.network));
  }, []);

  const outOfNetworkAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>): Array<SeparableAccumulationItem> => {
    return accumulations?.filter((accum) => !IN_NETWORK_STRINGS.includes(accum.network));
  }, []);

  const individualAccumulations = useCallback(
    (accumulations: Array<SeparableAccumulationItem>): Array<SeparableAccumulationItem> => {
      if (isFamilyCoverage) {
        // we only display individual accums on a family plan if they are embedded
        return accumulations?.filter((accum) => accum.is_embedded);
      } else {
        return accumulations;
      }
    },
    [isFamilyCoverage],
  );

  const familyAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
    if (isFamilyCoverage) {
      return accumulations;
    } else {
      return [];
    }
  }, []);

  const deductibleAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
    if (USE_CATEGORY_ACCUMS) {
      return accumulations?.filter((accum) => accum.type.includes(DEDUCTIBLE_STRING));
    } else {
      return accumulations?.filter((accum) => accum.type === DEDUCTIBLE);
    }
  }, []);

  const outOfPocketAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
    if (USE_CATEGORY_ACCUMS) {
      return accumulations?.filter((accum) => accum.type.includes(OUT_OF_POCKET_STRING));
    } else {
      return accumulations?.filter((accum) => accum.type === OUT_OF_POCKET);
    }
  }, []);

  const planMaxAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
    if (USE_CATEGORY_ACCUMS) {
      return accumulations?.filter((accum) => accum.type.includes(PLAN_MAXIMUM_STRING));
    } else {
      return accumulations?.filter((accum) => accum.type === PLAN_MAXIMUM);
    }
  }, []);

  const fertilityAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
    return accumulations?.filter((accum) => accum.type.includes(FERTILITY_STRING));
  }, []);

  return {
    inNetworkAccumulations: inNetworkAccumulations,
    outOfNetworkAccumulations: outOfNetworkAccumulations,
    individualAccumulations: individualAccumulations,
    familyAccumulations: familyAccumulations,
    deductibleAccumulations: deductibleAccumulations,
    outOfPocketAccumulations: outOfPocketAccumulations,
    planMaxAccumulations: planMaxAccumulations,
    fertilityAccumulations: fertilityAccumulations,
  };
};
